<template lang="html">
  <div>
    <div class="fsd-pills--skeleton">
      <div class="fsd-pills__headline--skeleton" />
      <div class="fsd-pills__actions--skeleton">
        <button class="fsd-pills__action" />
        <button
          class="fsd-pills__action fsd-pills__action--icon fsd-pills__action--right"
        >
          <svg-icon icon-name="filter" />
        </button>

        <button class="fsd-pills__action fsd-pills__action--icon">
          <svg-icon icon-name="sort-arrows-o" />
        </button>
        <button class="fsd-pills__action fsd-pills__action--icon">
          <svg-icon icon-name="trash" />
        </button>
      </div>
    </div>
    <div class="fsd-pills">
      <component
        :is="resultsSidebar || !isPageTypeJobList ? 'h2' : 'h1'"
        class="fsd-pills__headline"
      >
        {{ headline }}
        <div
          v-tracking-context="{ event_element: 'pills' }"
          class="fsd-pills__wrapper"
          :class="{ 'fsd-pills__wrapper--scrollable': allPillsVisible }"
        >
          <template v-if="searchProfile.customTags.length > 0">
            <div class="fsd-pills__subtitle">
              {{ filteredCustomTagsHeadline }}
            </div>
            <div
              :class="
                allPillsVisible
                  ? 'fsd-pills__container__unfolded'
                  : 'fsd-pills__container__folded'
              "
            >
              <transition-group
                ref="customPills"
                :name="searchContextEvaluated ? 'pills' : 'hidden-transition'"
                tag="span"
                class="fsd-pills__container__pills"
              >
                <span
                  v-for="tag in searchProfile.customTags"
                  :key="tag"
                  class="fsd-custom-pill"
                >
                  <span class="fsd-custom-pill__text">{{ tag }}</span>
                  <span
                    v-click-track="{
                      action_type: 'pill_custom_remove',
                      target: tag
                    }"
                    class="fsd-custom-pill__delete"
                    @click="searchProfileRemoveCustomTag(tag)"
                  >
                    <svg-icon
                      icon-name="close"
                      class-name="fsd-custom-pill__icon"
                    />
                  </span>
                </span>
              </transition-group>
            </div>
          </template>
          <template v-if="checkedCategoryTags.length > 0">
            <div class="fsd-pills__subtitle">
              {{ filteredTagsHeadline }}
            </div>
            <div
              :class="
                allPillsVisible
                  ? 'fsd-pills__container__unfolded'
                  : 'fsd-pills__container__folded'
              "
            >
              <transition-group
                ref="categoryPills"
                :name="searchContextEvaluated ? 'pills' : 'hidden-transition'"
                tag="span"
                class="fsd-pills__container__pills"
              >
                <span
                  v-for="tag in checkedCategoryTags"
                  :key="tag.key"
                  class="fsd-category-pill"
                >
                  <span class="fsd-category-pill__text">{{ tag.label }}</span>
                  <span
                    v-click-track="{
                      action_type: 'pill_remove',
                      target: tag.key
                    }"
                    class="fsd-category-pill__delete"
                    @click="searchProfileRemoveTag(tag.key)"
                  >
                    <svg-icon
                      icon-name="close"
                      class-name="fsd-category-pill__icon"
                    />
                  </span>
                </span>
              </transition-group>
            </div>
          </template>
        </div>
      </component>

      <div class="fsd-pills__actions">
        <button
          v-if="showPillsToggleButton"
          id="togglePillsButton"
          v-click-track="{
            action_type: `pills_show_all_toggle_${
              allPillsVisible ? 'on' : 'off'
            }`
          }"
          class="fsd-pills__action"
          @click="toggleShowAllPills"
        >
          {{ toggleButtonText() }}
        </button>
        <button
          v-click-track="{ action_type: 'filter_toggle_on' }"
          v-tooltip="{
            content: $options.filters.t('client.fused.pills.filter')
          }"
          class="fsd-pills__action fsd-pills__action--icon fsd-pills__action--right"
          :class="{
            'fsd-pills__action--active': checkedCategoryTags.length !== 0
          }"
          @click="filterToggle"
        >
          <svg-icon icon-name="filter" />
        </button>
        <button
          v-click-track="{
            action_type: `sort_toggle_${isSortVisible ? 'on' : 'off'}`
          }"
          v-tooltip="{ content: $options.filters.t('client.fused.pills.sort') }"
          class="fsd-pills__action fsd-pills__action--icon"
          :class="{ 'fsd-pills__action--active': sort !== 'STANDARD' }"
          @click="onSortToggle"
        >
          <svg-icon icon-name="sort-arrows-o" />
        </button>
        <button
          v-click-track="{ action_type: 'search_reset' }"
          v-tooltip="{
            content: $options.filters.t('client.fused.pills.clear')
          }"
          :disabled="isSearchEmpty"
          class="fsd-pills__action fsd-pills__action--icon"
          @click="searchReset"
        >
          <svg-icon icon-name="trash" />
        </button>
      </div>

      <sort-dropdown />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { manualTrack } from '@/vue/directives/tracking'
import SortDropdown from '@/vue/components/fused/sort_dropdown'
import debounce from 'lodash-es/debounce'
import some from 'lodash-es/some'

export default {
  components: { SortDropdown },
  props: {
    headline: { type: String, required: true }
  },
  data: () => ({
    hiddenPillsCount: 0,
    showPillsToggleButton: false,
    allPillsVisible: false
  }),
  computed: {
    ...mapState([
      'searchProfile',
      'resultsSidebar',
      'sort',
      'searchContextEvaluated'
    ]),
    ...mapGetters([
      'checkedCategoryTags',
      'isSortVisible',
      'isSearchEmpty',
      'isPageTypeJobList'
    ]),
    filteredCustomTagsHeadline() {
      return this.$options.filters.t(
        'client.fused.filtered_custom_tags_headline'
      )
    },
    filteredTagsHeadline() {
      return this.$options.filters.t('client.fused.filtered_tags_headline', {
        count: this.checkedCategoryTags.length
      })
    }
  },
  mounted() {
    this.$VueEventBus.on('globalBlur', this.onGlobalBlur)
    window.addEventListener(
      'resize',
      debounce(this.setupTogglePillsButton, 250, {
        leading: false,
        trailing: true
      })
    )
  },
  // The toggle pills button has to be rendered after all pills span elements. The rendering of pills spans is not ready
  // immediately after updated(), so we have to delay the rendering of the button.
  updated: debounce(
    function () {
      this.$nextTick(() => {
        this.setupTogglePillsButton()
      })
    },
    250,
    { leading: false, trailing: true }
  ),
  destroyed() {
    this.$VueEventBus.off('globalBlur', this.onGlobalBlur)
  },
  methods: {
    ...mapActions(['filterToggle', 'sortToggle', 'searchReset']),
    ...mapMutations(['searchProfileRemoveTag', 'searchProfileRemoveCustomTag']),
    onGlobalBlur($event) {
      if (!$event.sortToggle && this.isSortVisible) {
        this.sortToggle()
        manualTrack($event.target, { action_type: 'sort_toggle_off' })
      }
    },
    onSortToggle($event) {
      // We'll tag the click event so we don't blur (and therefore close the sort dropdown) on our own event
      $event.sortToggle = true
      manualTrack($event.target, {
        action_type: `sort_toggle_${this.isSortVisible ? 'off' : 'on'}`
      })
      this.sortToggle()
    },
    setupTogglePillsButton() {
      this.hiddenPillsCount = this.countAllHiddenPills()
      this.showPillsToggleButton = !!this.hiddenPillsCount
    },
    countAllHiddenPills() {
      return this.countHiddenCustomPills() + this.countHiddenCategoryPills()
    },
    /**
     * The pills are represented by <span> elements. Visible are pills in the first and second row, the rest is hidden.
     * The custom pills of second row have offsetTop === 93. If a custom pill have offsetTop > 93 then it is hidden.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetTop
     * @returns number of hidden custom pills, that is number of pills NOT rendered in first or second row
     */
    countHiddenCustomPills() {
      return !this.$refs.customPills
        ? 0
        : this.$refs.customPills.children.reduce(
            (acc, { elm: child }) => (child.offsetTop > 93 ? acc : acc - 1),
            this.$refs.customPills.children.length
          )
    },
    /**
     * The pills are represented by <span> elements. Visible are pills in the first and second row, the rest is hidden.
     * The offsetTop of category pills is dependent on the presence of custom pills:
     * 1. If no custom pills are rendered, then the category pills of second row have offsetTop === 93
     * 2. If one row of custom pills is rendered, then the category pills of second row have offsetTop === 153
     * 3. If two rows of custom pills are rendered, then the category pills of second row have offsetTop === 180
     * If a category pill have offsetTop larger than the offsetTop of category pills in second row, then it is hidden.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetTop
     * @returns number of hidden custom pills, that is number of pills NOT rendered in first or second row
     */
    countHiddenCategoryPills() {
      const customTagsHaveRow1 =
        this.$refs.customPills && this.$refs.customPills.children.length
      const customTagsHaveManyRows =
        this.$refs.customPills &&
        some(
          this.$refs.customPills.children,
          ({ elm: { offsetTop } }) => offsetTop >= 93
        )

      return !this.$refs.categoryPills
        ? 0
        : this.$refs.categoryPills.children.reduce((acc, { elm: child }) => {
            if (customTagsHaveManyRows) {
              return child.offsetTop > 180 ? acc : acc - 1
            } else if (customTagsHaveRow1) {
              return child.offsetTop > 153 ? acc : acc - 1
            } else {
              return child.offsetTop > 93 ? acc : acc - 1
            }
          }, this.$refs.categoryPills.children.length)
    },
    toggleButtonText() {
      return this.allPillsVisible
        ? this.$options.filters.t('client.fused.pills.hide_all')
        : this.$options.filters.t('client.fused.pills.show_all', {
            n: this.hiddenPillsCount
          })
    },
    toggleShowAllPills() {
      this.allPillsVisible = !this.allPillsVisible
    }
  }
}
</script>

<style>
.pills-item {
  display: inline-block;
  margin-right: 10px;
}

.hidden-transition-leave-active,
.hidden-transition-leave-to {
  display: none;
}

.pills-enter-active,
.pills-leave-active {
  transition: all 0.3s;
}

.pills-enter, .pills-leave-to /* .list-leave-active below version 2.1.8 */ {
  opacity: 0;
  transform: scale(0.5);
}

.fader-enter-active,
.fader-leave-active {
  transition: all 0.3s;
}

.fader-enter,
.fader-leave-to {
  opacity: 0;
}
</style>
